import type { AppointmentRecord } from '../types/appointment';
import { normalizeAppointment } from './normalize';
import {
  formatClientTags,
  formatDateString,
  formatServiceDurationString,
  getEndTimeISO,
  composeClientObj,
  composeStaffObj,
  composeServicesArr,
  determineColor,
  composeTitle,
  formatCreatedAt,
  normalizeKkmState,
  normalizePaymentMethods,
  normalizePaymentSummary,
  normalizeConsumablesStorage,
  normalizeTechnologicalCardsList,
  normalizeGoodsList,
  normalizeConsumablesRecorded,
  normalizeVisitHistorySection,
  deriveCardStatus
} from './utils';

import { DateTime } from 'luxon';
import { getCompanyRegistry } from '../company/companyRegistrySingleton';
import { hmFromMinutes, toUtcIsoFromSalon } from '../time';

/** Previous values of the fields `applyMove` rewrites, for rolling a failed move back. */
export type AppointmentMoveUndo = {
  id: string;
  fields: Record<string, any>;
};

export class AppointmentRegistry {
  private appointmentsById: Map<string, AppointmentRecord>;
  private loadedWeeks: Set<string>;
  private tzVersionUsed: number;

  constructor() {
    this.appointmentsById = new Map();
    this.loadedWeeks = new Set();
    // track current tz version and re-normalize on tz changes
    this.tzVersionUsed = getCompanyRegistry().getTimezoneVersion();
    getCompanyRegistry().onTimezoneChanged((tz) => {
      try {
        this.reNormalizeAll(tz);
        this.tzVersionUsed = getCompanyRegistry().getTimezoneVersion();
        // eslint-disable-next-line no-console
        console.log('[AppointmentRegistry] timezone changed, re-normalized appointments to', tz);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[AppointmentRegistry] error re-normalizing on tz change', e);
      }
    });
  }

  /**
   * Remove an appointment from the in-memory registry (no server IO).
   * Safe to call even if the id is unknown.
   */
  removeById(id: string | number): void {
    this.appointmentsById.delete(String(id));
  }

  /**
   * Creates a draft appointment object for use in the "create appointment" modal.
   * Only includes base, editable fields. Excludes all derived/normalized fields.
   * @param params Object with required base fields for the draft appointment.
   * @returns AppointmentRecord draft object
   */
  createDraftAppointment(params: {
    staffId: number,
    staffObj?: any,
    date: string,
    startTime: string,
    endTime: string,
    company_id?: number,
    client?: any,
    services?: any[],
    comment?: string,
    // Add more base fields as needed
  }): any {
    return {
      specialistId: params.staffId,
      staff: params.staffObj || null,
      startTime: params.startTime,
      endTime: params.endTime,
      date: params.date,
      company_id: params.company_id || null,
      client: params.client || {
        type: "client",
        id: "",
        name: "",
        surname: "",
        patronymic: "",
        phone: "",
        additional_phone: "",
        email: "",
        comment: "",
        client_tips: false,
        birthday: null,
        card_number: "",
        gender: 0,
        discount: 0,
        discount_percent: 0,
        confirm_method: 1,
        client_tags: []
      },
      client_id: params.client?.id || null,
      services: params.services || [],
      comment: params.comment || "",
      isNew: true,
      deleted: 0 as 0 | 1,
      // Add other base fields as needed, but do NOT include:
      // clientRecordStatistic, detailsSection, paymentSection, consumables, visitHistorySection
    };
  }

  /**
   * Returns the earliest and latest loaded appointment dates as { start, end }.
   * If no appointments are loaded, returns null.
   */
  getCacheRange(): { start: string; end: string } | null {
    const all = this.getAllAppointments();
    if (all.length === 0) return null;
    const dates = all
      .map(app => app.date)
      .filter(Boolean)
      .map(date => DateTime.fromISO(date));
    if (dates.length === 0) return null;
    const minDate = dates.reduce((min, d) => (d < min ? d : min), dates[0]);
    const maxDate = dates.reduce((max, d) => (d > max ? d : max), dates[0]);
    return {
      start: minDate.toISODate(),
      end: maxDate.toISODate(),
    };
  }

  /**
   * Returns true if the week for the given date (YYYY-MM-DD) is loaded.
   */
  isDateCached(date: string): boolean {
    const weekStart = AppointmentRegistry.getWeekStart(date);
    return this.loadedWeeks.has(weekStart);
  }

  /**
   * Mark a week as loaded (should be called after a successful fetch).
   */
  markWeekLoaded(weekStart: string) {
    this.loadedWeeks.add(weekStart);
  }

  /**
   * Returns all loaded week start dates (YYYY-MM-DD, Monday).
   */
  getLoadedWeeks(): string[] {
    return Array.from(this.loadedWeeks);
  }

  /**
   * Helper to get week start (Monday) for a given date string.
   */
  static getWeekStart(dateStr: string): string {
    const date = DateTime.fromISO(dateStr, { zone: 'UTC' });
    const day = date.weekday; // 1 = Monday, 7 = Sunday
    const diff = day === 7 ? -6 : 1 - day; // Adjust to get Monday
    const monday = date.plus({ days: diff });
    return monday.toISODate();
  }

  /**
   * Returns the full, unmodified appointment object for the given id.
   * This is the raw object as stored internally, not normalized or denormalized.
   * @param id Appointment id
   * @returns AppointmentRecord object or undefined if not found
   */
  public getAppointmentByIdFull(id: string): AppointmentRecord | undefined {
    return this.appointmentsById.get(id);
  }

  // denormalizeIncludedObject and normalizeAppointment moved to normalize.ts

  addAppointment(app: any) {
    // Keyed as a string because every reader — getAppointmentById, merge, removeById,
    // applyMove — looks the id up as one. API payloads carry string ids, so this only
    // matters when a record is added from code, where a numeric id would otherwise
    // become invisible to all of them.
    this.appointmentsById.set(String(app.id), app);
    // eslint-disable-next-line no-console
    //console.log(`[AppointmentRegistry] Added appointment: id=${app.id}, master_id=${masterId}, client_id=${clientId}, total=${totalCount}`);
  }

  /**
   * Returns a fully normalized Appointment object for UI consumption.
   * Fills all fields as per src/lib/types.ts using available data.
   */
  getAppointmentById(id: string): any | undefined {
    const raw = this.appointmentsById.get(id);
    if (!raw) return undefined;
    console.log('[getAppointmentById] Full raw appointment data for id=' + id + ':', raw);

    // Compute endTime as ISO string if possible
    const endTimeISO = getEndTimeISO(raw);

    // Compose client object
    const clientObj = composeClientObj(raw);

    // Compose staff object
    const staffObj = composeStaffObj(raw);

    // Compose services array
    const servicesArr = composeServicesArr(raw);

    // Determine color
    const color = determineColor(raw, servicesArr);

    // Compose title
    const title = composeTitle(raw, servicesArr);

    // --- Appointment Details Section fields ---

    // --- Visit History Section normalization ---
    const visitHistorySection = normalizeVisitHistorySection(raw);

    // Compute start and end time strings using salon timezone for display
    const startTimeISO = raw.datetime || raw.date || "";
    const timezone = getCompanyRegistry().getTimezone();
    const startTimeString = startTimeISO
      ? DateTime.fromISO(startTimeISO, { zone: 'UTC' }).setZone(timezone).toFormat('HH:mm')
      : "";
    const endTimeString = endTimeISO
      ? DateTime.fromISO(endTimeISO, { zone: 'UTC' }).setZone(timezone).toFormat('HH:mm')
      : "";

    // Calculate duration from startTime and endTime
    let durationMs = 0;
    if (startTimeISO && endTimeISO) {
      const start = DateTime.fromISO(startTimeISO, { zone: 'UTC' });
      const end = DateTime.fromISO(endTimeISO, { zone: 'UTC' });
      durationMs = end.diff(start).as('milliseconds');
    }
    let durationHours = 1;
    let durationMinutes = 0;
    if (durationMs > 0) {
      durationHours = Math.floor(durationMs / (1000 * 60 * 60));
      durationMinutes = Math.round((durationMs % (1000 * 60 * 60)) / (1000 * 60));
      // Clamp to allowed values
      if (durationHours < 1) durationHours = 1;
      if (durationHours > 12) durationHours = 12;
      if (durationMinutes < 0) durationMinutes = 0;
      if (durationMinutes > 55) durationMinutes = 55;
    }
    // Compose serviceDurationString
    let durationStr = "";
    if (durationMs > 0) {
      if (durationHours > 0 && durationMinutes > 0) {
        durationStr = `${durationHours}h ${durationMinutes}min`;
      } else if (durationHours > 0) {
        durationStr = `${durationHours}h`;
      } else if (durationMinutes > 0) {
        durationStr = `${durationMinutes}min`;
      } else {
        durationStr = "0min";
      }
      if (startTimeString && endTimeString) {
        durationStr += ` (${startTimeString} – ${endTimeString})`;
      }
    } else {
      // fallback to old logic
      durationStr = formatServiceDurationString(raw);
    }

    const normalized_app = {
      id: Number(raw.id),
      specialistId:
        raw.staff_id ||
        raw.staffId ||
        raw.master_id ||
        (raw.master && raw.master.id) || 0,
      startTime: startTimeString,
      endTime: endTimeString,
      date: startTimeISO ? DateTime.fromISO(startTimeISO, { zone: 'UTC' }).toISODate() : "",
      color,
      title,
      client: clientObj,
      client_id: clientObj?.id,
      company_id: raw.company_id,
      additional_phone: raw.client?.additional_phone || "",
      email: raw.client?.email || "",
      birthday: raw.client?.birthday || "",
      // Raw gender code for i18n translation in components (0 = not selected, 1 = male, 2 = female)
      gender: typeof raw.client?.gender === "number" ? raw.client.gender : 0,
      discount: typeof raw.client?.discount === "number" ? raw.client.discount : 0,
      card_number: raw.client?.card_number || "",
      client_comment: raw.client?.comment || "",
      // the persisted flag lives on the client card (client.is_blocked); the
      // top-level key is what the modal writes before save
      disableOnlineBooking: raw.client?.is_blocked === true || raw.disableOnlineBooking === true,
      balance: `0 ${getCompanyRegistry().getCurrencySymbol()}`,
      // Raw slug for i18n translation in components
      mobileAppStatus: "no_data",
      phone: raw.client?.phone || "",
      details: raw.comment || "",
      attendance: raw.attendance,
      confirmed: raw.confirmed,
      paid_full: raw.paid_full || 0,
      isNew: raw.isNew || false,
      staff: staffObj,
      services: servicesArr,
      comment: raw.comment || "",
      staff_name: raw.staff?.name || "",
      client_name: raw.client?.display_name || raw.client?.name || "",
      // New: clean node with raw counters from the JSON
      clientStats: {
        /** Successful visits count from JSON */
        success_visits_count: raw.client?.success_visits_count ?? 0,
        /** Failed visits count from JSON */
        fail_visits_count: raw.client?.fail_visits_count ?? 0,
        /** Total visits count (success + fail) */
        total_visits_count: (raw.client?.success_visits_count ?? 0) + (raw.client?.fail_visits_count ?? 0),
      },
      clientRecordStatistic: raw.clientRecordStatistic,
      // --- Expose details for modal section ---
      // NOTE: Raw status slugs are stored here. Components should use the i18n-aware
      // utility functions (getVisitStatusI18n, etc.) with their `t` function to translate.
      detailsSection: {
        // Backend provides "create_date" — convert & format in salon tz
        createdAt: formatCreatedAt(raw.create_date),
        // Who created the appointment: staff name or "клиент" if null
        createdUser: raw.created_user?.yuser_name ?? "Client",
        // Visit status based on paid_full / attendance / confirmed precedence
        visitStatus: (() => {
          const paidFull = raw.paid_full ?? 0;
          const attendance = raw.attendance ?? 0;
          const confirmed = raw.confirmed ?? 0;
          if (paidFull === 1 || paidFull === 2) return "Paid";
          if (paidFull === 0 && attendance === 1) return "Arrived";
          if (paidFull === 0 && attendance === 0 && confirmed === 1) return "Confirmed";
          return "Unconfirmed";
        })(),
        // Raw attendance status slug for i18n translation in components
        attendanceStatusSlug: raw.attendance_status_slug || null,
        isPaid: raw.is_paid || false,
        // Raw boolean flags for i18n translation in components
        isReminderSent: !!(raw.is_remind_sms_sent || raw.sms_now || raw.sms_before || raw.email_now || raw.is_client_notification_sent),
        reviewRequested: raw.review_requested || false,
        saleBillPrinted: raw.is_sale_bill_printed || false,
        prepaid: !!raw.prepaid,
        comment: raw.comment || "—",
        clientTags: formatClientTags(raw),
        dateString: formatDateString(raw),
        startTimeString,
        endTimeString,
        serviceDurationString: durationStr,
        durationHours,
        durationMinutes,
      },
      // --- Expose payment API info for modal section ---
      paymentSection: {
        kkmState: normalizeKkmState(raw),
        paymentMethods: normalizePaymentMethods(raw),
        paymentSummary: normalizePaymentSummary(raw),
        paidFull: raw.paid_full || 0,
        isPaid: (raw.paid_full === 1 || raw.paid_full === 2) ? true : (raw.is_paid || false)
      },
      is_paid: (raw.paid_full === 1 || raw.paid_full === 2) ? true : (raw.is_paid || false),
      // --- Expose consumables section for modal ---
      consumables: raw.consumables
        ? (() => {
          const storageDict = normalizeConsumablesStorage(raw.consumables);
          const techCards = normalizeTechnologicalCardsList(raw.consumables);
          return {
            storage: storageDict,
            technologicalCardsList: techCards,
            goodsList: normalizeGoodsList(raw.consumables, storageDict),
            recorded: normalizeConsumablesRecorded(raw.consumables),
          };
        })()
        : null,
      // --- Expose visit history section for modal ---
      visitHistorySection: visitHistorySection,
    };

    console.log('--> Single appointment full', raw)
    console.log('--> Single normalized', normalized_app)
    return normalized_app;
  }

  getAllAppointments(): any[] {
    return Array.from(this.appointmentsById.values());
  }

  getAppointmentsForDate(date: string): any[] {
    return this.getAllAppointments().filter(app => app.date === date);
  }

  /**
   * Returns appointments grouped by date within the specified date range.
   * The return value is an object where keys are date strings (YYYY-MM-DD),
   * and values are arrays of appointments for that date.
   */
  getAppointmentsForDateRange(startDate: string, endDate: string): Record<string, any[]> {
    const start = DateTime.fromISO(startDate, { zone: 'UTC' });
    const end = DateTime.fromISO(endDate, { zone: 'UTC' });
    const result: Record<string, any[]> = {};

    this.getAllAppointments().forEach(app => {
      const appDate = DateTime.fromISO(app.date, { zone: 'UTC' });
      // Use Luxon comparison to avoid browser timezone issues
      if (!appDate.isValid || !start.isValid || !end.isValid) return;
      if (appDate.toISODate() >= start.toISODate() && appDate.toISODate() <= end.toISODate()) {
        const dateKey = app.date;
        if (!result[dateKey]) {
          result[dateKey] = [];
        }
        result[dateKey].push(app);
      }
    });

    return result;
  }

  /**
   * Parses an incoming JSON response and merges appointments incrementally.
   * Deduplicates and updates appointments by ID, supporting incremental merging.
   * @param json API or mock response containing appointment data.
   */
  async parseResponse(json: any): Promise<void> {
    if (!json || !json.data || !json.included) return;
    // Ensure company timezone is ready before normalization
    await getCompanyRegistry().ready();
    const tz = getCompanyRegistry().getTimezone();
    this.tzVersionUsed = getCompanyRegistry().getTimezoneVersion();
    const byTypeId = (type: string, id: string) => json.included.find((i: any) => i.type === type && i.id === id);

    const addedCountByDate: Record<string, number> = {};

    json.data.forEach((dayEntry: any) => {
      const recordRefs: any[] = dayEntry.relationships?.records?.data || [];
      const recordIds: string[] = recordRefs.map((r: any) => r.id).filter(Boolean);

      const records: any[] = recordIds
        .map((recId: string) => byTypeId("record", recId))
        .filter(Boolean)
        .sort((a, b) => {
          const dateA = DateTime.fromISO(a.attributes.date, { zone: 'UTC' });
          const dateB = DateTime.fromISO(b.attributes.date, { zone: 'UTC' });
          return dateA.diff(dateB).as('milliseconds');
        });

      records.forEach((rec: any) => {
        const normalized = normalizeAppointment(rec, json.included, tz);
        // mark which tz version produced these minutes
        normalized._tzVersion = this.tzVersionUsed;
        // Attach stats before adding to registry
        this.addAppointment(normalized);

        const rawDate = normalized.date || (normalized.attributes?.date ? normalized.attributes.date : "unknown");
        const dateKey = rawDate.slice(0, 10); // Extract YYYY-MM-DD only
        addedCountByDate[dateKey] = (addedCountByDate[dateKey] || 0) + 1;
      });
    });

    // No need to attach client statistics globally anymore

    // Log how many appointments added per day
    const logParts = Object.entries(addedCountByDate).map(([date, count]) => `[${date}] - ${count}`);
    // eslint-disable-next-line no-console
    console.log(`[AppointmentRegistry] Appointments added per day: ${logParts.join(", ")}`);
  }

  /**
   * Re-normalize all stored appointments using provided salon tz.
   * We re-derive startMinOfDay/durationMin/startTime/endTime from isoStartUtc/isoEndUtc.
   */
  private reNormalizeAll(tz: string) {
    for (const [id, rec] of this.appointmentsById.entries()) {
      try {
        const isoStartUtc: string | undefined = rec.isoStartUtc || undefined;
        // Build a minimal wrapper record to feed the normalizer (it expects rec.attributes etc.)
        const wrapped = isoStartUtc
          ? { id: rec.id, type: rec.type, attributes: { datetime: isoStartUtc, date: rec.date, length: (rec.length ?? (rec.durationMin ? rec.durationMin * 60 : undefined)) } }
          : { ...rec };
        const renorm = normalizeAppointment(wrapped as any, [], tz);
        renorm._tzVersion = getCompanyRegistry().getTimezoneVersion();
        // Preserve non-time fields from existing record:
        this.appointmentsById.set(id, { ...rec, ...renorm });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[AppointmentRegistry] reNormalizeAll error', e);
      }
    }
  }

  // Removed global attachClientStatistics

  /**
   * Formats appointments for a given date (and optional staffId) to the Appointment[] shape.
   * This encapsulates the mapping/filtering logic previously in apiMock.ts.
   */
  formatAppointmentsForDate(date: string, staffId?: number): any[] {
    // Helper for color assignment (duplicated from apiMock for now)
    function determineColor(services: any[]) {
      if (!services || services.length === 0) return "bg-gray-200";
      // Robustly check for both top-level and nested category_id
      const categoryId = services[0]?.category_id ?? services[0]?.attributes?.category_id;

      switch (categoryId) {
        case 1104759:
          return "bg-blue-100";
        case 1104827:
          return "bg-orange-100";
        case 1167599:
          return "bg-green-200";
        default:
          return "bg-gray-200";
      }
    }

    let appointments = this.getAppointmentsForDate(date);
    if (typeof staffId === "number") {
      appointments = appointments.filter((a: any) =>
        a.staff_id === staffId ||
        a.staffId === staffId ||
        a.master_id === staffId ||
        a.master?.id === staffId
      );
    }
    return appointments.map((a: any) => {
      if (process.env.NODE_ENV !== "production") {
        if (typeof a.startMinOfDay !== "number" || typeof a.durationMin !== "number") {
          // eslint-disable-next-line no-console
          console.warn("[registry] appointment lacks normalized minutes", a);
        }
      }
      let services: any[] = Array.isArray(a.services) ? a.services : [];

      // Build a multi-line services title compatible with composeTitle:
      //   "Service A\n- Service B\n- Service C"
      const serviceTitles = services
        .map((s: any) =>
          s && typeof s.title === "string" ? s.title.trim() : ""
        )
        .filter((t: string) => t.length > 0);

      const servicesTitle =
        serviceTitles.length > 0
          ? serviceTitles
            .map((t, idx) => (idx === 0 ? t : `-\n${t}`))
            .join("\n")
          : "";

      return {
        id: Number(a.id),
        specialistId: a.staff_id || a.staffId || a.master_id || (a.master && a.master.id) || 0,
        // Drag & drop checks the target staff member against these before allowing a
        // move, so the ids travel with the card rather than being looked up from the
        // registry inside the view.
        serviceIds: services
          .map((s: any) => Number(s?.id))
          .filter((id: number) => Number.isFinite(id)),
        startTime: a.startTime,
        endTime: a.endTime,
        startMinOfDay: typeof a.startMinOfDay === "number" ? a.startMinOfDay : undefined,
        durationMin: typeof a.durationMin === "number" ? a.durationMin : undefined,
        color: a.color || determineColor(services),
        title: a.title || servicesTitle,
        details: a.details || a.comment || "",
        client: a.client_name || (a.client && a.client.name) || "",
        phone: a.phone || (a.client && a.client.phone) || "",
        isNew: a.isNew || false,
        status: deriveCardStatus(a), // <-- NEW: single glyph status for cards
        linked_record_ids: a.linked_record_ids || [], // <-- linked appointments for same client
        confirm_method: a.client?.confirm_method ?? 1,
        messenger: a.client?.messenger ?? undefined,
        confirm_ask_channel: a.confirm_ask_channel ?? null,
      };
    });
  }

  /**
   * Formats appointments for a list of dates to { [date]: Appointment[] }.
   * Each date's appointments are formatted using formatAppointmentsForDate.
   */
  formatAppointmentsForWeek(dates: string[]): Record<string, any[]> {
    const result: Record<string, any[]> = {};
    for (const date of dates) {
      result[date] = this.formatAppointmentsForDate(date);
    }
    return result;
  }

  /**
   * Formats appointments for a list of dates for a specific staff member.
   * Returns { [date]: Appointment[] } with only appointments for the given staffId.
   */
  formatAppointmentsForWeekForStaff(staffId: number, dates: string[]): Record<string, any[]> {
    const result: Record<string, any[]> = {};
    for (const date of dates) {
      result[date] = this.formatAppointmentsForDate(date, staffId);
    }
    return result;
  }


  /**
   * Merges incoming appointment data into the stored appointment by id.
   * Recursively updates only changed keys and adds new keys (including nested).
   * Does not remove any keys from the stored appointment.
   * @param incoming The incoming appointment object (from API).
   */
  merge(incoming: any): void {
    if (!incoming || !incoming.id) return;
    const id = String(incoming.id);
    const stored = this.appointmentsById.get(id);
    if (!stored) return;

    AppointmentRegistry.deepMerge(stored, incoming);

    // No-op: rely on raw counters from API (clientStats) instead of computed fake stats
  }

  /**
   * Rewrites the time and staff assignment of a stored appointment.
   *
   * Used by drag & drop to show the move before the server confirms it. Every field the
   * calendar reads for placement is rewritten together — leaving `startTime` in step with
   * `startMinOfDay`, say — so a half-applied move can never be rendered.
   *
   * Returns a snapshot of the previous values; pass it to `revertMove` if the save fails.
   */
  applyMove(
    id: string | number,
    patch: { date: string; startMin: number; staffId: number; staffName?: string }
  ): AppointmentMoveUndo | null {
    const key = String(id);
    const stored = this.appointmentsById.get(key) as any;
    if (!stored) return null;

    const durationMin = Number.isFinite(stored.durationMin)
      ? Number(stored.durationMin)
      : Math.max(1, Math.round(Number(stored.length ?? stored.seance_length ?? 0) / 60));

    const TOUCHED = [
      "date",
      "startTime",
      "endTime",
      "startMinOfDay",
      "isoStartUtc",
      "isoEndUtc",
      "datetime",
      "staff_id",
      "specialistId",
      "staff",
      "staff_name",
    ] as const;
    const undo: AppointmentMoveUndo = { id: key, fields: {} };
    for (const field of TOUCHED) {
      undo.fields[field] = stored[field];
    }

    const startTime = hmFromMinutes(patch.startMin);
    const endTime = hmFromMinutes(patch.startMin + durationMin);

    stored.date = patch.date;
    stored.startTime = startTime;
    stored.endTime = endTime;
    stored.startMinOfDay = patch.startMin;
    stored.staff_id = patch.staffId;
    stored.specialistId = patch.staffId;
    if (stored.staff && typeof stored.staff === "object") {
      stored.staff = {
        ...stored.staff,
        id: patch.staffId,
        ...(patch.staffName ? { name: patch.staffName } : {}),
      };
    }
    if (patch.staffName) stored.staff_name = patch.staffName;

    // UTC boundaries are what a timezone change re-normalizes from, so they have to
    // follow the move too or a later tz switch would resurrect the old time.
    try {
      const tz = getCompanyRegistry().getTimezone?.() || "UTC";
      stored.isoStartUtc = toUtcIsoFromSalon(patch.date, startTime, tz);
      stored.isoEndUtc = toUtcIsoFromSalon(patch.date, endTime, tz);
      stored.datetime = stored.isoStartUtc;
    } catch {
      // Leave the UTC fields alone if the salon zone cannot resolve them; the refetch
      // that follows the save will restore consistency.
    }

    return undo;
  }

  /** Restores the fields captured by `applyMove`. */
  revertMove(undo: AppointmentMoveUndo | null | undefined): void {
    if (!undo) return;
    const stored = this.appointmentsById.get(undo.id) as any;
    if (!stored) return;
    for (const [field, value] of Object.entries(undo.fields)) {
      stored[field] = value;
    }
  }

  /**
   * Recursively merges source into target, updating changed keys and adding new ones.
   * Arrays and primitives are replaced; objects are merged recursively.
   * Keys missing in source are not removed from target.
   */
  private static deepMerge(target: any, source: any): void {
    if (!source || typeof source !== "object") return;
    for (const key of Object.keys(source)) {
      const srcVal = source[key];
      const tgtVal = target[key];
      if (
        srcVal &&
        typeof srcVal === "object" &&
        !Array.isArray(srcVal) &&
        tgtVal &&
        typeof tgtVal === "object" &&
        !Array.isArray(tgtVal)
      ) {
        // Both are objects: merge recursively
        AppointmentRegistry.deepMerge(tgtVal, srcVal);
      } else {
        // For arrays, primitives, or if target is not object: replace
        target[key] = srcVal;
      }
    }
  }

}
